import toast from 'react-hot-toast';

// ダークモード検出（MUIテーマにアクセスできないユーティリティ用）
const isDarkMode = () => {
  // MUIテーマのdata属性を優先的にチェック
  const root = document.documentElement;
  const muiMode = root.getAttribute('data-mui-color-scheme') || root.getAttribute('data-theme');
  if (muiMode === 'dark') return true;
  if (muiMode === 'light') return false;
  // body上のクラス名チェック
  if (document.body.classList.contains('dark-mode')) return true;
  // フォールバック: OSのカラースキーム設定
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
};

// エラーメッセージマッピング
const ERROR_MESSAGES = {
  // ネットワークエラー
  'Network Error': {
    message: 'インターネット接続を確認してください',
    action: 'ネットワーク接続を確認してから再度お試しください'
  },
  'Failed to fetch': {
    message: 'サーバーに接続できません',
    action: 'しばらく時間をおいてから再度お試しください'
  },
  
  // HTTP ステータスエラー
  400: {
    message: '入力内容に誤りがあります',
    action: '入力内容を確認してから再度お試しください'
  },
  401: {
    message: 'ログインが必要です',
    action: '一度ログアウトして再度ログインしてください'
  },
  403: {
    message: 'この操作を実行する権限がありません',
    action: null
  },
  404: {
    message: '要求されたデータが見つかりません',
    action: null
  },
  422: {
    message: '入力データの形式が正しくありません',
    action: '入力内容を確認してから再度お試しください'
  },
  429: {
    message: 'リクエストが多すぎます',
    action: 'しばらく時間をおいてから再度お試しください'
  },
  500: {
    message: 'サーバーでエラーが発生しました',
    action: 'しばらく時間をおいてから再度お試しください'
  },
  502: {
    message: 'サーバーが一時的に利用できません',
    action: 'しばらく時間をおいてから再度お試しください'
  },
  503: {
    message: 'サーバーがメンテナンス中です',
    action: 'しばらく時間をおいてから再度お試しください'
  },
  504: {
    message: 'サーバーの応答が遅くなっています',
    action: 'しばらく時間をおいてから再度お試しください'
  },
  
  // 特定のエラーパターン
  'token_expired': {
    message: 'ログインの有効期限が切れました',
    action: '一度ログアウトして再度ログインしてください'
  },
  'validation_error': {
    message: '入力内容に誤りがあります',
    action: '必須項目を確認してから再度お試しください'
  },
  'permission_denied': {
    message: 'この操作を実行する権限がありません',
    action: null
  },
  
  // デフォルトエラー
  default: {
    message: '予期しないエラーが発生しました',
    action: 'しばらく時間をおいてから再度お試しください'
  }
};

// エラー種別の判定
export const getErrorType = (error) => {
  if (!error) return 'default';
  
  // ネットワークエラー
  if (error.message === 'Network Error' || error.message === 'Failed to fetch') {
    return error.message;
  }
  
  // HTTP ステータスエラー
  if (error.response && error.response.status) {
    return error.response.status;
  }
  
  // 特定のエラーメッセージパターン
  if (error.message) {
    if (error.message.includes('token') && error.message.includes('expired')) {
      return 'token_expired';
    }
    if (error.message.includes('validation') || error.message.includes('required')) {
      return 'validation_error';
    }
    if (error.message.includes('permission') || error.message.includes('権限')) {
      return 'permission_denied';
    }
  }
  
  return 'default';
};

// ユーザーフレンドリーなエラーメッセージを取得
export const getUserFriendlyErrorMessage = (error) => {
  const errorType = getErrorType(error);
  const errorInfo = ERROR_MESSAGES[errorType] || ERROR_MESSAGES.default;
  
  let message = errorInfo.message;
  
  // アクションがある場合は追加
  if (errorInfo.action) {
    message += `\n\n${errorInfo.action}`;
  }
  
  return message;
};

// エラーを表示する関数
export const showError = (error, customMessage = null) => {
  const message = customMessage || getUserFriendlyErrorMessage(error);

  // コンソールには詳細なエラー情報を出力
  console.error('Error occurred:', {
    error,
    userMessage: message,
    originalError: error?.response || error?.message || error
  });
  
  // ユーザーにはフレンドリーなメッセージを表示
  const dark = isDarkMode();
  toast.error(message, {
    duration: 6000,
    position: 'top-center',
    style: {
      background: dark ? 'rgba(244,67,54,0.15)' : '#fef2f2',
      border: dark ? '1px solid rgba(244,67,54,0.3)' : '1px solid #fecaca',
      color: dark ? '#ef9a9a' : '#dc2626',
      fontSize: '14px',
      padding: '12px 16px',
      borderRadius: '8px',
      maxWidth: '500px',
      whiteSpace: 'pre-line' // 改行を有効にする
    }
  });
};

// 成功メッセージを表示する関数
export const showSuccess = (message) => {
  const dark = isDarkMode();
  toast.success(message, {
    duration: 4000,
    position: 'top-center',
    style: {
      background: dark ? 'rgba(76,175,80,0.15)' : '#f0fdf4',
      border: dark ? '1px solid rgba(76,175,80,0.3)' : '1px solid #bbf7d0',
      color: dark ? '#a5d6a7' : '#166534',
      fontSize: '14px',
      padding: '12px 16px',
      borderRadius: '8px'
    }
  });
};

// 情報メッセージを表示する関数
export const showInfo = (message) => {
  const dark = isDarkMode();
  toast(message, {
    duration: 4000,
    position: 'top-center',
    style: {
      background: dark ? 'rgba(33,150,243,0.15)' : '#eff6ff',
      border: dark ? '1px solid rgba(33,150,243,0.3)' : '1px solid #bfdbfe',
      color: dark ? '#90caf9' : '#1e40af',
      fontSize: '14px',
      padding: '12px 16px',
      borderRadius: '8px'
    }
  });
};

// 警告メッセージを表示する関数
export const showWarning = (message) => {
  const dark = isDarkMode();
  toast(message, {
    duration: 5000,
    position: 'top-center',
    icon: '⚠️',
    style: {
      background: dark ? 'rgba(255,152,0,0.15)' : '#fffbeb',
      border: dark ? '1px solid rgba(255,152,0,0.3)' : '1px solid #fed7aa',
      color: dark ? '#ffcc80' : '#92400e',
      fontSize: '14px',
      padding: '12px 16px',
      borderRadius: '8px'
    }
  });
};

// 注意：古いトークンベースの認証インターセプター（setupAxiosInterceptors）は削除されました
// 現在はCookieベースの認証を使用しています（axiosConfig.jsを参照）
// ログアウト処理はHeader.jsとSidebar.jsで実装されています 